import React, { ComponentType, useCallback, useMemo, useState } from "react";

import { useIntl } from "react-intl";

import { Collapse, CollapsePanel } from "@mapmycustomers/ui";

import Option from "../types/Option";
import OptionGroup from "../types/OptionGroup";
import OptionValueEditorProps from "../types/OptionValueEditorProps";
import ValueEditorProps from "../types/ValueEditorProps";

import BulkApply from "./BulkApply";
import styles from "./OptionList.module.scss";

interface Props<T> extends ValueEditorProps<T[]> {
  getBulkUpdateText: (item: T) => string;
  isDefault: (value: T) => boolean;
  itemComponent: ComponentType<OptionValueEditorProps<T>>;
  needShowBulkUpdating: (value: number) => boolean;
  onBulkUpdate: (item: T) => void;
  optionGroups?: OptionGroup[];
  options: Option[];
}

const OptionList = <T extends { fieldValue: unknown }>({
  getBulkUpdateText,
  isDefault,
  itemComponent: ItemComponent,
  needShowBulkUpdating,
  onBulkUpdate,
  onChange,
  optionGroups,
  options,
  value,
}: Props<T>) => {
  const intl = useIntl();

  const valueToDisplayName = useMemo(
    () => new Map(options.map(({ label, value }) => [value, label])),
    [options]
  );

  const valueToGroupId = useMemo(
    () => new Map(options.map(({ groupId, value }) => [value, groupId])),
    [options]
  );

  const [lastChangedField, setLastChangedField] = useState<T | undefined>();

  const handleItemChange = useCallback(
    (item: T) => {
      onChange?.(value.map((value) => (value.fieldValue === item.fieldValue ? item : value)));
      setLastChangedField(item);
    },
    [onChange, setLastChangedField, value]
  );

  const handleBulkApply = useCallback(() => {
    if (lastChangedField) {
      onBulkUpdate(lastChangedField);
      setLastChangedField(undefined);
    }
  }, [lastChangedField, onBulkUpdate]);

  const renderOptionComponent = useCallback(
    (value) => (
      <div key={value.fieldValue}>
        <ItemComponent
          label={valueToDisplayName.get(String(value.fieldValue)) ?? String(value.fieldValue)}
          onChange={handleItemChange}
          value={value}
        />
        {lastChangedField &&
          String(value.fieldValue) === String(lastChangedField?.fieldValue) &&
          needShowBulkUpdating(value.fieldValue) && (
            <BulkApply
              label={getBulkUpdateText(lastChangedField)}
              onApply={handleBulkApply}
              onHide={() => setLastChangedField(undefined)}
            />
          )}
      </div>
    ),
    [
      handleItemChange,
      handleBulkApply,
      lastChangedField,
      needShowBulkUpdating,
      getBulkUpdateText,
      ItemComponent,
      valueToDisplayName,
    ]
  );

  const getGroupOptions = useCallback(
    (group: OptionGroup) =>
      value.filter(({ fieldValue }) => valueToGroupId.get(String(fieldValue)) === group.id),
    [valueToGroupId, value]
  );

  return (
    <div className={styles.container}>
      {optionGroups && (
        <Collapse expandIconPosition="end">
          {optionGroups.map((group) => (
            <CollapsePanel className={styles.groupCollapse} header={group.name} key={group.id}>
              {getGroupOptions(group).map(renderOptionComponent)}
            </CollapsePanel>
          ))}
        </Collapse>
      )}
      {!optionGroups &&
        value
          .filter((value) => isDefault(value))
          .map((value) => (
            <ItemComponent
              key="default"
              label={intl.formatMessage({
                id: "map.colorization.createEditSidebar.styleEditor.optional.defaultOption",
                defaultMessage: "Unknown (No value)",
                description:
                  "A default option to show in a list of options in CreateEdit Custom Pin Legend",
              })}
              onChange={handleItemChange}
              value={value}
            />
          ))}
      {!optionGroups && value.filter((value) => !isDefault(value)).map(renderOptionComponent)}
    </div>
  );
};

export default OptionList;
